import { ReactNode } from 'react';

import { useRelationshipTasks } from 'services/repositories/tasks/tasks';
import { RelationshipV2, TaskDTO } from 'support/types';
import { RelationshipStateBadge, TaskResponsibility } from 'components/RelationshipStateBadge/RelationshipStateBadge';

export const RelationshipBadge = ({
  relationship,
  currentPartnerId,
  children,
}: {
  relationship: RelationshipV2;
  currentPartnerId: string;
  children?: ReactNode;
}) => {
  const currentPartnerIsSender = relationship.sender_partner?.id === currentPartnerId;
  const currentPartnerIsRequester = relationship.trade_request?.requested_by === currentPartnerId;
  const otherPartner = currentPartnerIsSender ? relationship.receiver_partner : relationship.sender_partner;
  const status = relationship.status;

  const isTasksPending = status === 'TASKS_PENDING';
  const { data: tasksData } = useRelationshipTasks({
    variables: { relationshipId: isTasksPending ? relationship.id : undefined },
  });

  const taskResponsibility = getTaskResponsibility(isTasksPending ? tasksData?.data : undefined, currentPartnerId);

  return (
    <RelationshipStateBadge
      status={status}
      requester={currentPartnerIsRequester}
      sender={currentPartnerIsSender}
      taskResponsibility={taskResponsibility}
      otherPartner={otherPartner}
      senderIntegration={relationship.sender_integration?.production || undefined}
      receiverIntegration={relationship.receiver_integration?.production || undefined}
    >
      {children}
    </RelationshipStateBadge>
  );
};

const getTaskResponsibility = (tasks: Array<TaskDTO> | undefined, currentPartnerId: string): TaskResponsibility => {
  if (!tasks) {
    return null;
  }

  const hasCurrentPartnerTask = tasks.some((task) => task.responsible_partner?.id === currentPartnerId);
  if (hasCurrentPartnerTask) {
    return 'current_partner';
  }

  const hasOtherPartnerTask = tasks.some(
    (task) => task.responsible_partner !== null && task.responsible_partner !== undefined,
  );
  if (hasOtherPartnerTask) {
    return 'other_partner';
  }

  return 'procuros';
};
